/**
 * 编辑器聚焦工具函数
 * 用于处理contentEditable元素的聚焦和光标定位
 */

/**
 * 聚焦到编辑器内容末尾
 * @param element contentEditable元素
 */
export const focusToEnd = (element: HTMLElement | null) => {
  if (!element) return;
  
  try {
    element.focus();
    const range = document.createRange();
    const selection = window.getSelection();
    
    if (!selection) return;
    
    // 如果元素有子节点，使用第一个文本节点
    let textNode = element.firstChild;
    if (!textNode) {
      // 如果没有子节点，使用元素本身
      textNode = element;
    }
    
    // 如果是文本节点，直接使用
    if (textNode.nodeType === Node.TEXT_NODE) {
      range.setStart(textNode, textNode.textContent?.length || 0);
      range.setEnd(textNode, textNode.textContent?.length || 0);
    } else {
      // 否则选择整个内容并折叠到末尾
      range.selectNodeContents(textNode);
      range.collapse(false);
    }
    
    selection.removeAllRanges();
    selection.addRange(range);
  } catch (error) {
    console.warn('Focus to end failed:', error);
    // 降级处理：简单聚焦
    element.focus();
  }
};

/**
 * 聚焦到编辑器内容开头
 * @param element contentEditable元素
 */
export const focusToStart = (element: HTMLElement | null) => {
  if (!element) return;
  
  try {
    element.focus();
    const range = document.createRange();
    const selection = window.getSelection();
    
    if (!selection) return;
    
    // 如果元素有子节点，使用第一个文本节点
    let textNode = element.firstChild;
    if (!textNode) {
      // 如果没有子节点，使用元素本身
      textNode = element;
    }
    
    // 如果是文本节点，直接使用
    if (textNode.nodeType === Node.TEXT_NODE) {
      range.setStart(textNode, 0);
      range.setEnd(textNode, 0);
    } else {
      // 否则选择整个内容并折叠到开头
      range.selectNodeContents(textNode);
      range.collapse(true);
    }
    
    selection.removeAllRanges();
    selection.addRange(range);
  } catch (error) {
    console.warn('Focus to start failed:', error);
    // 降级处理：简单聚焦
    element.focus();
  }
};

/**
 * 聚焦到指定位置
 * @param element contentEditable元素
 * @param position 位置索引
 */
export const focusToPosition = (element: HTMLElement | null, position: number) => {
  if (!element) return;
  
  try {
    element.focus();
    const range = document.createRange();
    const selection = window.getSelection();
    
    if (!selection) return;
    
    // 如果元素有子节点，使用第一个文本节点
    let textNode = element.firstChild;
    if (!textNode) {
      // 如果没有子节点，使用元素本身
      textNode = element;
    }
    
    // 如果是文本节点，直接使用
    if (textNode.nodeType === Node.TEXT_NODE) {
      const maxPosition = textNode.textContent?.length || 0;
      const safePosition = Math.max(0, Math.min(position, maxPosition));
      range.setStart(textNode, safePosition);
      range.setEnd(textNode, safePosition);
    } else {
      // 否则选择整个内容并折叠到指定位置
      range.selectNodeContents(textNode);
      range.collapse(true);
    }
    
    selection.removeAllRanges();
    selection.addRange(range);
  } catch (error) {
    console.warn('Focus to position failed:', error);
    // 降级处理：简单聚焦
    element.focus();
  }
};

/**
 * 获取当前光标位置
 * @param element contentEditable元素
 * @returns 光标位置，如果没有选择则返回-1
 */
export const getCursorPosition = (element: HTMLElement | null): number => {
  if (!element) return -1;
  
  try {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return -1;
    
    const range = selection.getRangeAt(0);
    const textNode = range.startContainer;
    
    if (textNode.nodeType === Node.TEXT_NODE) {
      return range.startOffset;
    }
    
    return -1;
  } catch (error) {
    console.warn('Get cursor position failed:', error);
    return -1;
  }
};

/**
 * 在光标位置插入文本
 * @param element contentEditable元素
 * @param text 要插入的文本
 */
export const insertTextAtCursor = (element: HTMLElement | null, text: string) => {
  if (!element || !text) return;
  
  try {
    element.focus();
    document.execCommand('insertText', false, text);
  } catch (error) {
    console.warn('Insert text failed:', error);
    // 降级处理：在末尾添加文本
    const currentContent = element.textContent || '';
    element.textContent = currentContent + text;
    focusToEnd(element);
  }
};

/**
 * 设置编辑器内容并聚焦到末尾
 * @param element contentEditable元素
 * @param content 要设置的内容
 */
export const setContentAndFocusToEnd = (element: HTMLElement | null, content: string) => {
  if (!element) return;
  
  element.textContent = content;
  focusToEnd(element);
};

/**
 * 检查元素是否获得焦点
 * @param element 要检查的元素
 * @returns 是否获得焦点
 */
export const isElementFocused = (element: HTMLElement | null): boolean => {
  return element === document.activeElement;
}; 